import { Component, DestroyRef, HostListener, inject, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { NgxPermissionsService } from 'ngx-permissions';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { combineLatest, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter } from 'rxjs/operators';
import { CorporateFormsService } from '../../corporate/services/corporate-forms.service';
import { PermissionsService } from '../../services/permissions.service';
import { COMPANY_SETTINGS_SECTIONS } from '../../shared/navigation/company-settings-sections';
import { HeaderComponent } from '../header/header.component';
import { PortalApplicationsService } from '../../corporate/portal-applications/portal-applications.service';
import { ModulesService } from '../../services/modules.service';
import { IModule } from '../../models/module/IModule';
import { BcApp } from '../../shared/ui/app-switcher/bc-app-switcher.component';
import { FinderItem, PageFinderService } from '../../shared/page-finder/page-finder.service';
import { BcLayoutService } from '../../core/ui/bc-layout.service';
import { BcTab } from '../../shared/ui/tab-bar/bc-tab-bar.component';
import { BRAND } from '../../config/brand';

/** SPEC.md › App switcher grouping (a proposal until product confirms it). */
const APP_GROUPS: Record<number, string> = {
  1: 'Workspace',
  2: 'Finance & tax', 7: 'Finance & tax', 12: 'Finance & tax', 6: 'Finance & tax', 8: 'Finance & tax', 10: 'Finance & tax',
  3: 'Compliance & audit', 9: 'Compliance & audit', 15: 'Compliance & audit', 14: 'Compliance & audit',
  4: 'People & operations', 5: 'People & operations', 13: 'People & operations', 11: 'People & operations',
};
const GROUP_ORDER = ['Workspace', 'Finance & tax', 'Compliance & audit', 'People & operations', 'Other'];
const COLLAPSED_KEY = 'bc.sidebarCollapsed';

/** badge: which count to show beside the link ('portalNeedsAction' = New from the portal › Needs action). */
interface NavLink { name: string; icon?: string; route: string; queryParam?: any; queryParams?: Record<string, any> | null; children?: any[] | null; notesOnly?: boolean; badge?: 'portalNeedsAction'; }

/**
 * Option D app shell (docs/redesign, SPEC.md › Layout): dark sidebar with the app switcher, org
 * switcher, Create, the current app's pages (only the active one expanded) and the account menu;
 * light top bar with breadcrumb, Ctrl K search, contacts, notes, messages and notifications.
 * Rendered by AppComponent on every route and screen size; phones and tablets get their own layouts
 * (docs/redesign/mobile, see `layout` below).
 *
 * It EXTENDS HeaderComponent rather than re-implementing it. The header owns the menu, the
 * global search and its scope filters, the company picker, notifications, module switching and
 * the account entries — about 1,800 lines of behaviour the redesign must not change. The
 * subclass keeps all of it and only swaps the template, plus the bits of state the new layout
 * needs (expanded sections, the collapsed rail, the app list for the switcher, the breadcrumb).
 */
@Component({
  selector: 'bc-app-shell',
  templateUrl: './bc-app-shell.component.html',
  styleUrls: ['./bc-app-shell.component.scss'],
  standalone: false,
})
export class BcAppShellComponent extends HeaderComponent implements OnInit, OnChanges {
  private readonly nav = inject(Router);
  private readonly apps$ = inject(ModulesService);

  /** Icon-rail sidebar. A per-viewer preference, so it lives in localStorage. */
  collapsed = readCollapsed();
  /** Parent items (by name) whose children are showing. */
  expanded = new Set<string>();
  /** The current app's pages, with View/Create sub-levels folded (see foldChildren). */
  navItems: NavLink[] = [];
  /** Every "create-form" entry in the menu, for the Create button. */
  createItems: NavLink[] = [];
  apps: BcApp[] = [];
  /** Notes is hidden for Corporate Viewers and notes-restricted tenants (canSeeGeneralNotes). */
  canSeeNotesNav = false;
  notesUnread = 0;
  /** Portal clients waiting on staff (Records › New from the portal). Refreshed by that page too. */
  private readonly portalApplications = inject(PortalApplicationsService);
  readonly portalNeedsAction = this.portalApplications.needsActionCount;
  /** The top bar's "What are you looking for?" button opens it; the card itself is in AppComponent. */
  readonly pageFinder = inject(PageFinderService);

  badgeFor(link: NavLink): number {
    return link.badge === 'portalNeedsAction' ? this.portalNeedsAction() : 0;
  }

  /** Sidebar items the user may see. */
  get visibleNavItems(): NavLink[] {
    return this.navItems.filter(i => !i.notesOnly || this.canSeeNotesNav);
  }
  currentAppId: number | null = null;
  breadcrumb: string[] = [];

  private appsRun = 0;
  private currentPath = '';
  private currentQuery: Record<string, string> = {};

  /** Record pages (client records, organisation-company settings) use the icon rail (SPEC.md). */
  private onRecordPage = false;
  /** Expanded by hand on the current record page. Not saved: the next record opens as a rail. */
  private expandedOnRecord = false;
  /**
   * Notes opened in a new tab (a note's "Open in new tab", a shared note link, the top bar's Notes
   * button) is used like a chat window, so it gets the whole width: no sidebar, only the top bar.
   * Reaching Notes from the sidebar's Notes item stays an ordinary page with the sidebar. The tab
   * counts as opened for Notes when its first load was /tagged-notes; leaving Notes in that tab
   * brings the sidebar back.
   */
  isChatPage = false;
  private readonly openedForNotes = window.location.pathname === '/tagged-notes';

  /**
   * Phone / tablet / desktop (docs/redesign/mobile). Desktop (>= 1280) keeps the behaviour above.
   * Tablets always show the 80px rail with labels; opening it (a section, the chevron) lays the full
   * sidebar over the page. Phones have no sidebar: an app bar, bottom tabs and the full sidebar
   * as a drawer.
   */
  readonly layout = inject(BcLayoutService);

  /** The full sidebar is laid over the page (phone drawer, tablet rail opened). */
  get overlayNav(): boolean {
    return (this.layout.isPhone() || this.layout.useRail()) && this.isMobileMenuOpen;
  }

  get isRail(): boolean {
    if (this.layout.isPhone()) return false;
    if (this.layout.useRail()) return !this.isMobileMenuOpen;
    return this.onRecordPage ? !this.expandedOnRecord : this.collapsed;
  }

  /** Tablet rail: icons carry a short label underneath. The desktop's collapsed rail stays icon-only. */
  get railLabels(): boolean { return this.isRail && this.layout.useRail(); }

  /** Phone app bar: the search replaces the bar while open (mockups/phone/MSearch). */
  phoneSearchOpen = false;
  /** Phone app bar: the less-used tools (contacts, notes, messages, presence…) on a second row. */
  phoneToolsOpen = false;

  /** Phone app bar title: the page, else the app. */
  get pageTitle(): string {
    return this.breadcrumb[this.breadcrumb.length - 1] || BRAND.name;
  }

  /** Phone bottom tabs (mockups/phone): rebuilt when the URL, the app or a count changes. */
  phoneTabs: BcTab[] = [];
  get moreTabActive(): boolean { return !this.phoneTabs.some(t => t.active); }

  get showTabBar(): boolean {
    return this.layout.isPhone() && !this.layout.immersive() && this.loginDisplay && !this.isOnboarding && !this.isChatPage;
  }

  /** A page holds the whole phone screen (BcLayoutService.immersive): no app bar either. */
  get phoneImmersive(): boolean { return this.layout.isPhone() && this.layout.immersive(); }

  get userInitials(): string {
    const u = this.currentUser as any;
    const fromName = [u?.firstName, u?.lastName].filter(Boolean).map((s: string) => s[0]).join('');
    return (fromName || u?.email?.substring(0, 2) || 'U').toUpperCase();
  }

  get userDisplayName(): string {
    const u = this.currentUser as any;
    return [u?.firstName, u?.lastName].filter(Boolean).join(' ') || u?.email || '';
  }

  override ngOnInit(): void {
    // Subscribe before the header's init emits the first module.
    this.changeModule.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(id => {
      this.currentAppId = id;
      this.updateBreadcrumb();
    });
    super.ngOnInit();

    this.canSeeNotes$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(v => {
      this.canSeeNotesNav = !!v;
      this.refreshTabs();
    });
    this.meetingNotesPanelService.unreadCount$.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((n: any) => {
        this.notesUnread = Number(n) || 0;
        this.refreshTabs();
      });

    // The sidebar's "New from the portal" count: only while Corporate (whose menu has the link) is
    // open, re-read when the organisation changes.
    combineLatest([this.changeModule, this.companiesService.getManagedCompanyIdObsValue()])
      .pipe(
        filter(([moduleId, companyId]) => moduleId === 1 && !!companyId),
        distinctUntilChanged(([, a], [, b]) => a === b),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(([, companyId]) => this.portalApplications.refreshCounts(companyId));

    // The org id is part of each app's new-tab link, so rebuild once this tab knows its org.
    combineLatest([this.apps$.getModules(), this.permissionsChanged$, this.companiesService.getManagedCompanyIdObsValue()])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([modules]) => void this.buildApps(modules));

    this.pageFinder.registerSource(this, () => this.finderItems());
    this.destroyRef.onDestroy(() => this.pageFinder.unregisterSource(this));

    this.readUrl(this.nav.url);
    this.nav.events.pipe(filter(e => e instanceof NavigationEnd), takeUntilDestroyed(this.destroyRef))
      .subscribe((e: NavigationEnd) => {
        this.readUrl(e.urlAfterRedirects);
        this.isMobileMenuOpen = false;
        this.phoneSearchOpen = false;
        this.phoneToolsOpen = false;
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['menu']) {
      this.navItems = (this.menu || []).map(item => ({ ...item, children: this.foldChildren(item.children) }));
      this.createItems = collectCreateItems(this.menu || []);
      this.expandActive();
      this.updateBreadcrumb();
    }
  }

  // ---- Sidebar ----

  toggleRail(): void {
    // Tablets: the rail is fixed; "expanding" lays the full sidebar over the page instead.
    if (this.layout.useRail()) {
      this.isMobileMenuOpen ? this.closeMobileNav() : this.openMobileNav();
      return;
    }
    if (this.onRecordPage) {
      this.expandedOnRecord = !this.expandedOnRecord;
      return;
    }
    this.collapsed = !this.collapsed;
    try { localStorage.setItem(COLLAPSED_KEY, this.collapsed ? '1' : '0'); } catch { /* per-viewer nicety only */ }
  }

  isExpanded(item: NavLink): boolean { return this.expanded.has(item.name); }

  toggleSection(item: NavLink): void {
    if (this.isRail) {
      // The rail has no room for children: open the sidebar on the section instead.
      this.toggleRail();
      this.expanded.add(item.name);
      return;
    }
    this.expanded.has(item.name) ? this.expanded.delete(item.name) : this.expanded.add(item.name);
  }

  /** A child is active when its route and every query param it carries match the URL. */
  isChildActive(child: NavLink): boolean {
    if (this.currentPath !== '/' + child.route.replace(/^\/+/, '')) return false;
    const params = { ...(child.queryParams || {}) };
    if (child.queryParam !== null && child.queryParam !== undefined) params['type'] = child.queryParam;
    // A page opened without ?tab= shows its first sub-page, so the first child owns it.
    if ('tab' in params && this.currentQuery['tab'] === undefined) {
      const section = this.navItems.find(i => i.children?.includes(child));
      const first = section?.children?.find((c: NavLink) => c.route === child.route);
      return first === child;
    }
    return Object.entries(params).every(([k, v]) => this.currentQuery[k] === String(v));
  }

  /** Same-tab switch through the header's own selectModule (navigate + reload, as today). */
  onOpenApp(app: BcApp): void {
    if (app.id === this.currentAppId) return;
    this.selectModule(app.id);
  }

  /** Where focus goes back to when the drawer / overlay sidebar closes. */
  private navOpener: HTMLElement | null = null;

  /** Phone drawer / tablet overlay: focus moves into it, and back to the opener on close. */
  openMobileNav(): void {
    this.navOpener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.isMobileMenuOpen = true;
    // After the drawer renders: the first control (the app switcher) gets focus.
    setTimeout(() => document.querySelector<HTMLElement>('bc-app-shell .sidebar button, bc-app-shell .sidebar a')?.focus());
  }

  closeMobileNav(): void {
    if (!this.isMobileMenuOpen) return;
    this.isMobileMenuOpen = false;
    const opener = this.navOpener;
    this.navOpener = null;
    // Not when the opener has gone (the rail button that became the full sidebar's own).
    if (opener?.isConnected) setTimeout(() => opener.focus());
  }

  toggleMobileNav(event?: Event): void {
    event?.stopPropagation();
    this.isMobileMenuOpen ? this.closeMobileNav() : this.openMobileNav();
  }

  /** The header's own Escape handler still closes the search panel; this one closes the drawer. */
  @HostListener('document:keydown.escape', ['$event'])
  onEscape(event: Event): void {
    // The app switcher (inside the drawer) closes itself first; one Escape, one layer.
    if ((event.target as Element | null)?.closest?.('bc-app-switcher [role="dialog"]')) return;
    if (this.overlayNav) this.closeMobileNav();
    else if (this.phoneSearchOpen) this.closePhoneSearch();
  }

  openPhoneSearch(): void {
    this.phoneSearchOpen = true;
    this.phoneToolsOpen = false;
    setTimeout(() => this.searchInput?.nativeElement.focus());
  }

  closePhoneSearch(): void {
    this.phoneSearchOpen = false;
    this.onEscapeKey();
  }

  focusMain(event: Event): void {
    event.preventDefault();
    document.getElementById('main')?.focus();
  }

  // ---- Top bar ----

  /** Ctrl K / ⌘K: jump to search. The old header showed the hint but never bound the key. */
  @HostListener('document:keydown.control.k', ['$event'])
  @HostListener('document:keydown.meta.k', ['$event'])
  onSearchShortcut(event: Event): void {
    event.preventDefault();
    this.searchInput?.nativeElement.focus();
    this.searchInput?.nativeElement.select();
  }

  /**
   * The first crumb (the app's name, "Home" for Corporate) goes to the app's landing page: the
   * dashboard's Overview for Corporate, the same page the app switcher opens for the others.
   */
  goToAppHome(event?: MouseEvent): void {
    if (event && (event.ctrlKey || event.metaKey || event.shiftKey || event.button === 1)) return;
    event?.preventDefault();
    if (this.currentAppId == null) return;
    this.nav.navigate(this.appHomeCommands, { queryParams: this.appHomeParams });
  }

  get appHomeHref(): string {
    if (this.currentAppId == null) return '/';
    return this.nav.serializeUrl(this.nav.createUrlTree(this.appHomeCommands, { queryParams: this.appHomeParams }));
  }

  private get appHomeCommands(): any[] {
    return this.moduleLandingRoute(this.currentAppId!) ?? [''];
  }

  private get appHomeParams(): Record<string, any> {
    const landing = this.moduleLandingRoute(this.currentAppId!);
    return landing ? { module: this.currentAppId } : { module: this.currentAppId, tab: 'overview' };
  }

  /** Company Settings shortcuts in the organisation menu (same list as the dashboard's). */
  readonly companySettingsSections = COMPANY_SETTINGS_SECTIONS;
  private readonly corporateAccess = inject(PermissionsService);

  /**
   * Admins of this tab's organisation get the settings menu on the organisation crumb; everyone
   * else only sees the name. /settings/company/:id is behind pagePermissionGuard, so a non-admin
   * would only be bounced to '/'.
   */
  get canOpenCompanySettings(): boolean {
    return this.corporateAccess.isCorporateAdmin();
  }

  /** Opens this tab's organisation in Company Settings, at `step` when given, in a new tab. */
  openCurrentCompanySettings(step?: string): void {
    const companyId = this.currentCompanyIdFromStorage || this.companiesService.managedCompanyId;
    if (!companyId) return;
    this.companyForms.getInternalCompanyFormId(companyId).pipe(catchError(() => of(null))).subscribe(formId => {
      if (!formId) {
        this.notify.open('No internal client form found for this company.', 'Close', { duration: 3000 });
        return;
      }
      const urlTree = this.nav.createUrlTree(['/settings/company', companyId], {
        queryParams: { submittedFormId: formId, org: companyId, ...(step ? { step } : {}) },
      });
      window.open(this.nav.serializeUrl(urlTree), '_blank');
    });
  }
  private readonly companyForms = inject(CorporateFormsService);
  private readonly notify = inject(MatSnackBar);

  // ---- Page finder ----

  /**
   * The sidebar's places for the "What are you looking for?" card: this app's pages (sub-pages
   * included, collapsed or not), Create, the other apps and the account settings. Each one opens
   * through the same handler as its sidebar link.
   */
  private finderItems(): FinderItem[] {
    const pages = this.visibleNavItems.flatMap((item): FinderItem[] => item.children
      ? item.children.map(child => ({
          label: child.name, hint: item.name, keywords: item.name, group: 'Go to',
          run: () => this.onNavChildClick(child),
        }))
      : [{ label: item.name, icon: item.icon, group: 'Go to', run: () => this.onNavItemClick(item) }]);

    const create: FinderItem[] = this.createItems.map(item => ({
      label: item.name, group: 'Create', keywords: 'new add create', run: () => this.onNavChildClick(item),
    }));

    const apps: FinderItem[] = this.apps
      .filter(app => app.status === 'available' && app.id !== this.currentAppId)
      .map(app => ({
        label: app.label, hint: app.externalUrl ? 'New tab' : undefined, group: 'Apps',
        keywords: 'app module switch ' + app.group,
        run: () => app.externalUrl ? window.open(app.externalUrl, '_blank', 'noopener') : this.onOpenApp(app),
      }));

    const settings: FinderItem[] = [
      { label: 'Profile', url: '/settings/user' },
      { label: 'Notification settings', url: '/settings/notifications' },
      { label: 'Organisation settings', url: '/settings/organization' },
      { label: 'My drafts', url: '/drafts' },
    ].map(s => ({ label: s.label, group: 'Settings', keywords: 'account', run: () => void this.nav.navigateByUrl(s.url) }));

    return [...pages, ...create, ...apps, ...settings];
  }

  // ---- Internals ----

  private get permissionsChanged$() { return this.ngxPermissions.permissions$; }
  private readonly ngxPermissions = inject(NgxPermissionsService);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Level-2 items with their own children (Records › Individuals › View / Create) become one link
   * to their "View" page. The Create entries move to the Create button, so the sidebar stays two
   * levels deep.
   */
  private foldChildren(children: any[] | null | undefined): NavLink[] | null {
    if (!children?.length) return null;
    return children.map(child => {
      if (!child.children?.length) return child;
      const view = child.children.find((c: NavLink) => !isCreateItem(c)) ?? child.children[0];
      return { ...view, name: child.name, icon: child.icon, children: null };
    });
  }

  /**
   * Resolves access itself rather than reading the header's moduleAccess map: that map is filled
   * asynchronously off the same permission changes, so reading it here could see the previous set.
   * Runs overlap on quick permission changes; only the newest one publishes (as in the header).
   */
  private async buildApps(modules: IModule[]): Promise<void> {
    const run = ++this.appsRun;
    const access = await Promise.all(modules.map(m => this.apps$.hasModuleAccess(m.id)));
    if (run !== this.appsRun) return;
    const origin = window.location.origin;
    this.apps = modules
      // Only apps the user can open, plus coming-soon ones (shown disabled). Unlike the old grid,
      // no greyed-out tiles for modules without access.
      .filter((m, i) => m.status === 'coming-soon' || access[i])
      .map(m => ({
        id: m.id,
        label: m.name,
        icon: m.icon,
        group: APP_GROUPS[m.id] ?? 'Other',
        status: m.status === 'coming-soon' ? 'soon' as const : 'available' as const,
        externalUrl: m.externalUrl || undefined,
        newTabHref: m.externalUrl || m.status === 'coming-soon'
          ? undefined
          : origin + this.nav.serializeUrl(this.nav.createUrlTree([''], { queryParams: this.moduleTabParams(m.id) })),
      }))
      .sort((a, b) => GROUP_ORDER.indexOf(a.group) - GROUP_ORDER.indexOf(b.group));
    this.updateBreadcrumb();
  }

  /**
   * Query params for opening a module in a new tab. Organizations are per tab (OrgScopeStore), and
   * the switcher opens with noopener, so without ?org= the new tab fell back to the first
   * organization opened in the browser instead of the one this tab is on.
   */
  private moduleTabParams(moduleId: number): Record<string, number> {
    const org = this.companiesService.managedCompanyId;
    return org ? { module: moduleId, org } : { module: moduleId };
  }

  private readUrl(url: string): void {
    const tree = this.nav.parseUrl(url);
    const previousPath = this.currentPath;
    this.currentPath = '/' + (tree.root.children['primary']?.segments.map(s => s.path).join('/') ?? '');
    this.currentQuery = tree.queryParams as Record<string, string>;
    if (this.currentPath !== previousPath) this.expandedOnRecord = false;
    this.onRecordPage = /^\/(edit-form|settings\/company)\//.test(this.currentPath);
    this.isChatPage = this.openedForNotes && this.currentPath === '/tagged-notes';
    this.expandActive();
    this.updateBreadcrumb();
  }

  /** Only the active section is expanded (SPEC.md): navigating collapses the others. */
  private expandActive(): void {
    // A section is open when the page on screen is one of its children (exact match, the same check
    // that highlights the child) or its own route. Sections the user opened by hand stay open until
    // they close them or move to another section.
    const active = this.navItems.filter(i => i.children && this.isSectionActive(i)).map(i => i.name);
    if (!active.length) return;
    this.expanded = new Set(active);
  }

  private isSectionActive(item: NavLink): boolean {
    return (item.children || []).some((c: NavLink) => this.isChildActive(c)) || this.isNavItemActive(item);
  }

  private updateBreadcrumb(): void {
    const app = this.apps.find(a => a.id === this.currentAppId)?.label;
    const section = this.navItems.find(i => i.children ? this.isSectionActive(i) : this.isNavItemActive(i));
    const page = section?.children?.find(c => this.isChildActive(c));
    this.breadcrumb = [app, section?.name, page?.name].filter((s): s is string => !!s);
    this.refreshTabs();
  }

  /**
   * Corporate gets the mockup's tabs: Home · Clients · Planner · Notes (+ More). Its client records
   * count as Clients. Other apps get Home and Notes; their own pages are in the drawer (More).
   */
  private refreshTabs(): void {
    const path = this.currentPath;
    const tabs: BcTab[] = [{
      label: 'Home', icon: 'home',
      commands: this.currentAppId == null ? [''] : this.appHomeCommands,
      queryParams: this.currentAppId == null ? null : this.appHomeParams,
      active: this.currentAppId != null && path === '/' + (this.appHomeCommands[0] || '').replace(/^\/+/, ''),
    }];
    if (this.currentAppId === 1) {
      tabs.push(
        { label: 'Clients', icon: 'groups', commands: ['/forms-table'], queryParams: { category: 'clients' },
          active: /^\/(forms-table|edit-form\/|create-form\/)/.test(path) },
        { label: 'Planner', icon: 'checklist', commands: ['/task-planner'], active: path.startsWith('/task-planner') },
      );
    }
    if (this.canSeeNotesNav) {
      tabs.push({ label: 'Notes', icon: 'sticky_note_2', commands: ['/tagged-notes'], active: path.startsWith('/tagged-notes'),
        badge: this.notesUnread || undefined, badgeLabel: 'unread' });
    }
    this.phoneTabs = tabs;
  }
}

function readCollapsed(): boolean {
  try { return localStorage.getItem(COLLAPSED_KEY) === '1'; } catch { return false; }
}

function isCreateRoute(route: string | undefined): boolean {
  return !!route && route.startsWith('create-form/');
}

/** A create entry: the create form, or a list page told to start its create (?create=, e.g. Shelf Names). */
function isCreateItem(item: any): boolean {
  return isCreateRoute(item?.route) || !!item?.queryParams?.create;
}

function collectCreateItems(items: any[]): NavLink[] {
  const out: NavLink[] = [];
  const walk = (nodes: any[] | null | undefined, parent?: string) => (nodes || []).forEach(n => {
    if (isCreateItem(n)) {
      // "Create" alone under Individuals reads as "New individual"; named ones keep their name.
      const name = n.name === 'Create' && parent ? `New ${singular(parent)}` : n.name.replace(/^Create /, 'New ');
      out.push({ ...n, name: sentence(name) });
    }
    walk(n.children, n.name);
  });
  walk(items);
  return out;
}

function singular(name: string): string {
  return name.replace(/ies$/, 'y').replace(/s$/, '').toLowerCase();
}

function sentence(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}
